package com.upcominds.expedite.features.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.ApiException
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.data.user.UserRepository
import com.upcominds.expedite.dto.SocialAccountDTO
import com.upcominds.expedite.model.User
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class ProfileUiState(
    val loading: Boolean = false,
    val error: String? = null,
    val user: User? = null,
    val socialAccounts: List<SocialAccountDTO> = emptyList()
)

class ProfileViewModel(
    private val userRepository: UserRepository
) : ViewModel() {
    private val _uiState = MutableStateFlow(ProfileUiState())
    val uiState: StateFlow<ProfileUiState> = _uiState.asStateFlow()

    fun load() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val user = userRepository.getCurrentUser()
                val socialAccounts = userRepository.getSocialAccounts()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    user = user,
                    socialAccounts = socialAccounts
                )
            } catch (e: Exception) {
                logMobileError("ProfileViewModel.load", e)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = toUserMessage(e)
                )
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    private fun toUserMessage(error: Exception): String {
        val apiError = error as? ApiException ?: return error.message ?: "Failed to load profile."
        return when (apiError.statusCode) {
            401 -> "Your session has expired. Please sign in again."
            in 500..599 -> "Profile is temporarily unavailable. Please try again."
            else -> apiError.message.ifBlank { "Failed to load profile." }
        }
    }
}
